import { buildPayzheBtpSettingsPayload } from './buildSettingsPayload';
import {
  fetchPayzheBtpStatus,
  getStoredPayzheBtpToken,
  handshakePayzheBtp,
  NativeBtpClientError,
  postPayzheBtpServiceRestart,
  postPayzheBtpSettings,
  probePayzheBtpReachable,
  probePayzheBtpVersion,
  reachabilityReasonToErrorKey,
  savePayzheBtpToken,
  type PayzheBtpReachabilityReason,
} from './nativeBtpClient';
import { connectPayzheBtpWebSocket } from './nativeBtpWebSocket';
import { sleep, wakePayzheBtpApp } from './mobilePlatform';
import { fetchPayzheBtpPolicy, type ResolvedPayzheBtpPolicy } from './payzheBtpSettings';
import { PayzheBtpError } from './PayzheBtpError';
import { shouldRestartPayzheBtpService } from './serviceRestartPolicy';
import {
  getPayzheBtpRequireService,
} from './bluetoothProviderSettings';
import { notifyPayzheBtpJobTerminal } from './payzheBtpJobWait';
import type {
  PayzheBtpApiMessage,
  PayzheBtpJobStatus,
  PayzheBtpProviderState,
  PayzheBtpStatusPayload,
  PayzheBtpVersionInfo,
} from './types';
import { compareSemver, isApiVersionCompatible } from './versionUtils';
import { PAYZHE_BTP_EXPECTED_API_VERSION } from './bridgeVersion';

export type PayzheBtpEnsureReadyOptions = {
  /** `auto` (default): probe HTTP first, wake only when bridge is down. `true`: always wake. `false`: never wake. */
  wakeApp?: boolean | 'auto';
};

const WS_RECONNECT_MAX_DELAY_MS = 30_000;
const RECONCILE_INTERVAL_MS = 30_000;

export type PayzheBtpProviderSnapshot = {
  state: PayzheBtpProviderState;
  status: PayzheBtpStatusPayload | null;
  version: PayzheBtpVersionInfo | null;
  policy: ResolvedPayzheBtpPolicy | null;
  wsConnected: boolean;
  lastErrorKey: string | null;
  token: string;
  printingJobId: string | null;
  isPrinting: boolean;
  /** Soft hint when APK is below latest but still above minimum. */
  versionWarning: 'apk_update_available' | null;
};

type Listener = () => void;

function createInitialSnapshot(): PayzheBtpProviderSnapshot {
  return {
    state: 'idle',
    status: null,
    version: null,
    policy: null,
    wsConnected: false,
    lastErrorKey: null,
    token: getStoredPayzheBtpToken(),
    printingJobId: null,
    isPrinting: false,
    versionWarning: null,
  };
}

const PAYZHE_BTP_JOB_STATUSES: readonly PayzheBtpJobStatus[] = [
  'pending',
  'queued',
  'waiting-for-printer',
  'waiting-for-bluetooth',
  'printing',
  'completed',
  'failed',
  'cancelled',
  'retry-scheduled',
  'expired',
  'processing',
];

function isPayzheBtpJobStatus(value: string | undefined): value is PayzheBtpJobStatus {
  return typeof value === 'string' && (PAYZHE_BTP_JOB_STATUSES as readonly string[]).includes(value);
}

class PayzheBtpProviderImpl {
  private snapshot: PayzheBtpProviderSnapshot = createInitialSnapshot();

  private listeners = new Set<Listener>();

  private wsDisconnect: (() => void) | null = null;

  private wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private wsReconnectAttempt = 0;

  private reconcileTimer: ReturnType<typeof setInterval> | null = null;

  private ensureReadyPromise: Promise<void> | null = null;

  private bridgeReadyThisSession = false;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): PayzheBtpProviderSnapshot {
    return this.snapshot;
  }

  isBridgeReadyThisSession(): boolean {
    return this.bridgeReadyThisSession;
  }

  private mapReachabilityState(reason?: PayzheBtpReachabilityReason): PayzheBtpProviderState {
    if (reason === 'connection_refused' || reason === 'timeout') {
      return 'not_running';
    }
    return 'unreachable';
  }

  private notify(): void {
    for (const fn of this.listeners) {
      try {
        fn();
      } catch {
        /* ignore */
      }
    }
  }

  private patch(partial: Partial<PayzheBtpProviderSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...partial };
    this.notify();
  }

  private clearWsReconnectTimer(): void {
    if (this.wsReconnectTimer) {
      globalThis.clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }
  }

  private scheduleWsReconnect(): void {
    const state = this.snapshot.state;
    if (state !== 'ready' && state !== 'service_stopped') return;
    const token = getStoredPayzheBtpToken();
    if (!token || this.wsReconnectTimer) return;

    const delay = Math.min(1000 * 2 ** this.wsReconnectAttempt, WS_RECONNECT_MAX_DELAY_MS);
    this.wsReconnectTimer = globalThis.setTimeout(() => {
      this.wsReconnectTimer = null;
      this.wsReconnectAttempt += 1;
      this.connectWebSocket(token);
    }, delay);
  }

  async detect(): Promise<boolean> {
    return this.probeBridge();
  }

  async probeBridge(): Promise<boolean> {
    const result = await probePayzheBtpReachable();
    if (!result.reachable || !result.token) {
      const nextState = this.mapReachabilityState(result.reason);
      this.patch({
        state: nextState,
        wsConnected: false,
        lastErrorKey: reachabilityReasonToErrorKey(result.reason),
      });
      return false;
    }

    savePayzheBtpToken(result.token);
    this.bridgeReadyThisSession = true;
    this.patch({
      token: result.token,
      version: result.version ?? this.snapshot.version,
      lastErrorKey: null,
    });

    if (!this.snapshot.wsConnected) {
      this.connectWebSocket(result.token);
      this.startReconcile();
    }

    void this.refreshStatus();
    return true;
  }

  private resolveVersionWarning(
    version: PayzheBtpVersionInfo,
    policy: ResolvedPayzheBtpPolicy,
  ): 'apk_update_available' | null {
    if (compareSemver(version.version, policy.latestVersion) < 0) {
      return 'apk_update_available';
    }
    return null;
  }

  private async checkVersionPolicy(
    version: PayzheBtpVersionInfo,
    policy: ResolvedPayzheBtpPolicy,
  ): Promise<'apk_update_available' | null> {
    if (version.compatibility?.compatible === false) {
      this.patch({
        state: 'outdated',
        version,
        policy,
        lastErrorKey: 'bluetoothPrinter.payzheBtp.errors.incompatibleWeb',
        versionWarning: null,
      });
      throw new PayzheBtpError(
        'Zeettu web version incompatible with payzhe BTP',
        'incompatible_web',
        'bluetoothPrinter.payzheBtp.errors.incompatibleWeb',
        'update_web',
      );
    }
    if (
      version.apiVersion &&
      !isApiVersionCompatible(version.apiVersion, PAYZHE_BTP_EXPECTED_API_VERSION)
    ) {
      this.patch({
        state: 'outdated',
        version,
        policy,
        lastErrorKey: 'bluetoothPrinter.payzheBtp.errors.outdated',
        versionWarning: null,
      });
      throw new PayzheBtpError(
        'payzhe BTP API version mismatch',
        'outdated',
        'bluetoothPrinter.payzheBtp.errors.outdated',
        'update_apk',
      );
    }
    if (compareSemver(version.version, policy.minimumVersion) < 0) {
      this.patch({
        state: 'outdated',
        version,
        policy,
        lastErrorKey: 'bluetoothPrinter.payzheBtp.errors.outdated',
        versionWarning: null,
      });
      throw new PayzheBtpError(
        'payzhe BTP version below minimum',
        'outdated',
        'bluetoothPrinter.payzheBtp.errors.outdated',
        'update_apk',
      );
    }
    return this.resolveVersionWarning(version, policy);
  }

  private connectWebSocket(token: string): void {
    this.clearWsReconnectTimer();
    this.wsDisconnect?.();
    this.wsDisconnect = connectPayzheBtpWebSocket(
      {
        onOpen: () => {
          this.wsReconnectAttempt = 0;
          this.patch({ wsConnected: true });
        },
        onClose: () => {
          this.patch({ wsConnected: false });
          this.scheduleWsReconnect();
        },
        onError: () => {
          this.patch({ wsConnected: false });
          this.scheduleWsReconnect();
        },
        onMessage: (message: PayzheBtpApiMessage) => this.handleWsMessage(message),
      },
      token,
    );
  }

  private handleWsMessage(message: PayzheBtpApiMessage): void {
    const type = message.type;
    if (type === 'BridgeReady') {
      void this.probeBridge();
      return;
    }
    if (
      type === 'ConnectionStateChanged' ||
      type === 'PrinterConnected' ||
      type === 'PrinterDisconnected' ||
      type === 'PrinterRecovered' ||
      type === 'PrinterLost' ||
      type === 'QueueUpdated' ||
      type === 'DiagnosticsUpdated' ||
      type === 'status' ||
      type === 'status.snapshot'
    ) {
      void this.refreshStatus();
    }
    if (type === 'JobStarted' || type === 'JobQueued') {
      const jobId = this.readJobIdFromPayload(message.payload);
      this.patch({ printingJobId: jobId, isPrinting: true });
      void this.refreshStatus();
    }
    if (type === 'JobCompleted' || type === 'JobFailed' || type === 'JobCancelled') {
      const jobId = this.readJobIdFromPayload(message.payload);
      const payloadStatus =
        typeof message.payload?.status === 'string' ? message.payload.status : undefined;
      const fallbackStatus: PayzheBtpJobStatus =
        type === 'JobCompleted' ? 'completed' : type === 'JobCancelled' ? 'cancelled' : 'failed';
      const status: PayzheBtpJobStatus = isPayzheBtpJobStatus(payloadStatus)
        ? payloadStatus
        : fallbackStatus;
      const lastError =
        typeof message.payload?.lastError === 'string'
          ? message.payload.lastError
          : typeof message.payload?.error === 'string'
            ? message.payload.error
            : undefined;
      if (jobId) {
        notifyPayzheBtpJobTerminal(jobId, status, lastError);
      }
      this.patch({
        printingJobId: this.snapshot.printingJobId === jobId ? null : this.snapshot.printingJobId,
        isPrinting: false,
      });
      void this.refreshStatus();
    }
    if (type === 'BackgroundServiceStarted' || type === 'BackgroundServiceStopped') {
      void this.refreshStatus();
    }
  }

  private readJobIdFromPayload(payload: Record<string, unknown> | undefined): string | null {
    if (!payload) return null;
    const jobId = payload.jobId ?? payload.id;
    return typeof jobId === 'string' ? jobId : null;
  }

  async refreshStatus(): Promise<PayzheBtpStatusPayload | null> {
    const token = getStoredPayzheBtpToken();
    if (!token) return null;
    try {
      const status = await fetchPayzheBtpStatus(token);
      const serviceRunning =
        status.serviceRunning ??
        (status.serverState === 'running' || status.connectionState !== 'unknown');
      const lifecycleFailed = status.serviceLifecycle === 'failed';
      const nextState: PayzheBtpProviderState =
        this.snapshot.state === 'outdated'
          ? 'outdated'
          : getPayzheBtpRequireService() && (!serviceRunning || lifecycleFailed) && !status.connected
            ? 'service_stopped'
            : 'ready';
      this.patch({ status, state: nextState });
      return status;
    } catch {
      return null;
    }
  }

  private startReconcile(): void {
    if (this.reconcileTimer) return;
    this.reconcileTimer = globalThis.setInterval(() => {
      void this.refreshStatus();
    }, RECONCILE_INTERVAL_MS);
  }

  private stopReconcile(): void {
    if (this.reconcileTimer) {
      globalThis.clearInterval(this.reconcileTimer);
      this.reconcileTimer = null;
    }
  }

  async ensureReady(options?: PayzheBtpEnsureReadyOptions): Promise<void> {
    if (this.ensureReadyPromise) {
      return this.ensureReadyPromise;
    }

    this.ensureReadyPromise = this.doEnsureReady(options).finally(() => {
      this.ensureReadyPromise = null;
    });
    return this.ensureReadyPromise;
  }

  private async shouldWakeForEnsure(options?: PayzheBtpEnsureReadyOptions): Promise<boolean> {
    const mode = options?.wakeApp ?? 'auto';
    if (mode === true) return true;
    if (mode === false) return false;
    const probe = await probePayzheBtpReachable();
    if (probe.reachable) {
      if (probe.token) {
        savePayzheBtpToken(probe.token);
        this.patch({ token: probe.token });
      }
      this.bridgeReadyThisSession = true;
      return false;
    }
    this.bridgeReadyThisSession = false;
    return true;
  }

  /** After a bridge deep-link wake, poll until localhost HTTP answers or timeout. */
  private async waitForBridgeAfterWake(maxMs = 15_000): Promise<boolean> {
    const started = Date.now();
    while (Date.now() - started < maxMs) {
      const probe = await probePayzheBtpReachable();
      if (probe.reachable) {
        if (probe.token) {
          savePayzheBtpToken(probe.token);
          this.patch({ token: probe.token });
        }
        return true;
      }
      await sleep(500);
    }
    return false;
  }

  private async doEnsureReady(options?: PayzheBtpEnsureReadyOptions): Promise<void> {
    const shouldWake = await this.shouldWakeForEnsure(options);
    if (options?.wakeApp === true) {
      this.bridgeReadyThisSession = false;
    }
    this.patch({ state: 'checking', lastErrorKey: null });

    if (shouldWake) {
      wakePayzheBtpApp();
      const bridgeUp = await this.waitForBridgeAfterWake();
      if (!bridgeUp) {
        await sleep(2000);
      }
    }

    const policy = this.snapshot.policy ?? (await fetchPayzheBtpPolicy());
    this.patch({ policy });

    let lastErrorKey = 'bluetoothPrinter.payzheBtp.errors.unreachable';
    let lastNetworkFailure = false;
    const maxAttempts = 8;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const handshake = await handshakePayzheBtp();
        savePayzheBtpToken(handshake.token);
        this.patch({ token: handshake.token });

        const version = await probePayzheBtpVersion(handshake.token);
        const versionWarning = await this.checkVersionPolicy(version, policy);

        let status = await fetchPayzheBtpStatus(handshake.token);
        if (shouldRestartPayzheBtpService(status)) {
          await postPayzheBtpServiceRestart(handshake.token).catch(() => undefined);
          status = await fetchPayzheBtpStatus(handshake.token);
        }

        try {
          await postPayzheBtpSettings(buildPayzheBtpSettingsPayload(), handshake.token);
        } catch {
          /* settings sync is best-effort; bridge stays usable */
        }
        this.connectWebSocket(handshake.token);
        this.startReconcile();

        const serviceRunning =
          status.serviceRunning ?? status.serverState === 'running';
        const lifecycleFailed = status.serviceLifecycle === 'failed';
        const state: PayzheBtpProviderState =
          getPayzheBtpRequireService() && (!serviceRunning || lifecycleFailed) && !status.connected
            ? 'service_stopped'
            : 'ready';

        this.patch({
          state,
          status,
          version,
          policy,
          lastErrorKey: null,
          versionWarning,
        });
        this.bridgeReadyThisSession = true;
        return;
      } catch (error) {
        if (error instanceof PayzheBtpError && (error.code === 'outdated' || error.code === 'incompatible_web')) {
          throw error;
        }
        if (error instanceof NativeBtpClientError && error.status) {
          lastNetworkFailure = false;
          lastErrorKey = error.message.startsWith('bluetoothPrinter.')
            ? error.message
            : 'bluetoothPrinter.payzheBtp.errors.bridgeError';
        } else {
          lastNetworkFailure = true;
          lastErrorKey =
            error instanceof Error && error.message.startsWith('bluetoothPrinter.')
              ? error.message
              : reachabilityReasonToErrorKey('connection_refused');
        }
        await sleep(Math.min(1500 + attempt * 500, 4000));
      }
    }

    this.bridgeReadyThisSession = false;
    const finalState: PayzheBtpProviderState = lastNetworkFailure ? 'not_running' : 'unreachable';
    this.patch({ state: finalState, lastErrorKey });
    throw new PayzheBtpError(lastErrorKey, finalState, lastErrorKey, 'wake_app');
  }

  disconnectWebSocket(): void {
    this.clearWsReconnectTimer();
    this.wsDisconnect?.();
    this.wsDisconnect = null;
    this.stopReconcile();
    this.patch({ wsConnected: false });
  }

  reset(): void {
    this.disconnectWebSocket();
    this.bridgeReadyThisSession = false;
    this.snapshot = createInitialSnapshot();
    this.notify();
  }
}

export const payzheBtpProvider = new PayzheBtpProviderImpl();

export function getPayzheBtpProviderSnapshot(): PayzheBtpProviderSnapshot {
  return payzheBtpProvider.getSnapshot();
}

export function subscribePayzheBtpProvider(listener: Listener): () => void {
  return payzheBtpProvider.subscribe(listener);
}
